import { useCallback, useState } from 'react'
import {
  useMarkInterviewPassed,
  useMarkInterviewFailed,
  useMarkInterviewNoShow,
  useMarkInterviewForReschedule,
} from './backend/interviews'
import { toast } from '../lib/shadcn/sonner'
import type { OutcomeAction } from '../components/interviews/DecisionActions'

const SUCCESS_LABEL: Record<OutcomeAction, string> = {
  passed: 'Interview marked passed',
  failed: 'Interview marked failed',
  no_show: 'Interview marked as no-show',
  reschedule: 'Interview sent for reschedule',
}

// Wires the four SECURITY DEFINER mutations to a single submit handler.
// The acting manager is resolved server-side; the frontend only passes notes.
export function useInterviewDecisions() {
  const markPassed = useMarkInterviewPassed()
  const markFailed = useMarkInterviewFailed()
  const markNoShow = useMarkInterviewNoShow()
  const markReschedule = useMarkInterviewForReschedule()
  const [busyId, setBusyId] = useState<string | null>(null)

  const submit = useCallback(
    async (
      interviewId: string,
      action: OutcomeAction,
      notes: string | null,
      onDone?: () => void,
    ) => {
      const runner =
        action === 'passed'
          ? markPassed.trigger
          : action === 'failed'
            ? markFailed.trigger
            : action === 'no_show'
              ? markNoShow.trigger
              : markReschedule.trigger

      setBusyId(interviewId)
      try {
        await runner({ interviewId, notes }).result
        toast.success(SUCCESS_LABEL[action])
        onDone?.()
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        toast.error('Could not record decision', { description: msg })
      } finally {
        setBusyId(null)
      }
    },
    [markPassed.trigger, markFailed.trigger, markNoShow.trigger, markReschedule.trigger],
  )

  return { submit, busyId }
}
